import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './TourForm.css';

const TourForm = ({ mode = 'admin', ownerToken = '', ownerPhone = '', onSuccess, onCancel }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOwnerMode = mode === 'owner';
  const isEdit = !!id && !isOwnerMode;
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState({
    title: '',
    agencyName: '',
    description: '',
    shortDescription: '',
    duration: '',
    suitableFor: '',
    city: '',
    state: '',
    country: 'India',
    address: '',
    displayAddress: '',
    latitude: '',
    longitude: '',
    cityLatitude: '',
    cityLongitude: '',
    whatsappNumber: '',
    phone: ownerPhone || '',
    email: '',
    website: '',
    availableDates: 'all',
    availableDatesType: 'all',
    slotConfigStartTime: '09:00',
    slotConfigEndTime: '18:00',
    slotConfigSlotDurationMinutes: '30',
    importantInfo: '',
    otherInfo: '',
    currency: 'INR',
    status: isOwnerMode ? 'pending' : 'active',
    featured: false,
    verified: false
  });

  const [activityOptions, setActivityOptions] = useState([]);
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [optionInput, setOptionInput] = useState({
    optionName: '',
    pricePerPerson: '',
    cutPrice: '',
    cancellationPolicyTag: '',
    description: ''
  });

  const [timeOptions, setTimeOptions] = useState([]);
  const [timeInput, setTimeInput] = useState('');
  const [specificDates, setSpecificDates] = useState([]);
  const [specificDateInput, setSpecificDateInput] = useState('');

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');

  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityInput, setCityInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headerTitle = isEdit ? 'Edit Tour / Activity' : (isOwnerMode ? 'Register your tour' : 'Add New Tour / Activity');

  useEffect(() => {
    if (isEdit && id) {
      fetchTour();
    }
  }, [id, isEdit]);

  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const fetchTour = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/tours/${id}`);
      const tour = response.data.data;
      setFormData({
        title: tour.title || '',
        agencyName: tour.agencyName || '',
        description: tour.description || '',
        shortDescription: tour.shortDescription || '',
        duration: tour.duration || '',
        suitableFor: tour.suitableFor || '',
        city: tour.city || '',
        state: tour.state || '',
        country: tour.country || 'India',
        address: tour.address || '',
        displayAddress: tour.displayAddress || tour.address || '',
        latitude: tour.latitude ?? '',
        longitude: tour.longitude ?? '',
        cityLatitude: tour.cityLatitude ?? '',
        cityLongitude: tour.cityLongitude ?? '',
        availableDates: tour.availableDates || 'all',
        availableDatesType: tour.availableDatesType || 'all',
        slotConfigStartTime: tour.slotConfig?.startTime ?? '09:00',
        slotConfigEndTime: tour.slotConfig?.endTime ?? '18:00',
        slotConfigSlotDurationMinutes: tour.slotConfig?.slotDurationMinutes ?? 30,
        importantInfo: tour.importantInfo || '',
        otherInfo: tour.otherInfo || '',
        whatsappNumber: tour.whatsappNumber || '',
        phone: tour.phone || '',
        email: tour.email || '',
        website: tour.website || '',
        currency: tour.currency || 'INR',
        status: tour.status || 'active',
        featured: tour.featured || false,
        verified: tour.verified || false
      });
      setActivityOptions(tour.activityOptions || []);
      setTimeOptions(tour.timeOptions || []);
      setSpecificDates(tour.specificDates?.length ? tour.specificDates.map(d => {
        const date = typeof d === 'string' ? d : (d?.toISOString?.() || d);
        return date.split('T')[0];
      }) : []);
      setExistingImages(tour.images || []);
      setFeaturedImage(tour.featuredImage || '');
      setCityInput(tour.city || '');
    } catch (err) {
      setError('Failed to fetch tour details');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCityInputChange = async (e) => {
    const value = e.target.value;
    setCityInput(value);
    setFormData(prev => ({ ...prev, city: value }));
    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value }
        });
        setCitySuggestions(response.data.data || []);
        setShowCitySuggestions(true);
      } catch (err) {
        if (err.response?.status !== 400) setCitySuggestions([]);
      }
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleCitySelect = async (place) => {
    setCityInput(place.description);
    setFormData(prev => ({ ...prev, city: place.description }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      const placeDetails = response.data.data;
      setFormData(prev => ({
        ...prev,
        city: place.description,
        state: placeDetails.state || prev.state,
        country: placeDetails.country || prev.country,
        cityLatitude: placeDetails.latitude ?? prev.cityLatitude,
        cityLongitude: placeDetails.longitude ?? prev.cityLongitude
      }));
    } catch (err) {
      console.error('Error fetching city place details:', err);
    }
  };

  const handleAddOption = () => {
    if (!optionInput.optionName || !optionInput.pricePerPerson) return;
    const newOption = {
      optionName: optionInput.optionName,
      pricePerPerson: parseFloat(optionInput.pricePerPerson),
      cutPrice: optionInput.cutPrice ? parseFloat(optionInput.cutPrice) : null,
      cancellationPolicyTag: optionInput.cancellationPolicyTag || '',
      description: optionInput.description || ''
    };
    if (editingOptionIndex !== null) {
      const updated = [...activityOptions];
      updated[editingOptionIndex] = newOption;
      setActivityOptions(updated);
      setEditingOptionIndex(null);
    } else {
      setActivityOptions(prev => [...prev, newOption]);
    }
    setOptionInput({
      optionName: '',
      pricePerPerson: '',
      cutPrice: '',
      cancellationPolicyTag: '',
      description: ''
    });
  };

  const handleEditOption = (index) => {
    const opt = activityOptions[index];
    setOptionInput({
      optionName: opt.optionName || '',
      pricePerPerson: opt.pricePerPerson !== undefined ? String(opt.pricePerPerson) : '',
      cutPrice: opt.cutPrice != null ? String(opt.cutPrice) : '',
      cancellationPolicyTag: opt.cancellationPolicyTag || '',
      description: opt.description || ''
    });
    setEditingOptionIndex(index);
  };

  const handleRemoveOption = (index) => {
    if (editingOptionIndex === index) setEditingOptionIndex(null);
    setActivityOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTime = () => {
    if (timeInput.trim()) {
      setTimeOptions(prev => [...prev, timeInput.trim()]);
      setTimeInput('');
    }
  };

  const handleRemoveTime = (index) => {
    setTimeOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSpecificDate = () => {
    if (specificDateInput.trim()) {
      setSpecificDates(prev => prev.includes(specificDateInput.trim()) ? prev : [...prev, specificDateInput.trim()].sort());
      setSpecificDateInput('');
    }
  };

  const handleRemoveSpecificDate = (index) => {
    setSpecificDates(prev => prev.filter((_, i) => i !== index));
  };

  const generateSlotPreview = () => {
    const start = formData.slotConfigStartTime || '09:00';
    const end = formData.slotConfigEndTime || '18:00';
    const duration = parseInt(formData.slotConfigSlotDurationMinutes, 10) || 30;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMins = sh * 60 + (sm || 0);
    const endMins = eh * 60 + (em || 0);
    const slots = [];
    for (let m = startMins; m + duration <= endMins; m += duration) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      const eh2 = Math.floor((m + duration) / 60);
      const em2 = (m + duration) % 60;
      slots.push(
        `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')} - ${String(eh2).padStart(2, '0')}:${String(em2).padStart(2, '0')}`
      );
    }
    return slots;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    setImagePreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleRemoveNewImage = (index) => {
    if (imagePreviews[index]) URL.revokeObjectURL(imagePreviews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSetFeaturedFromNew = (index) => setFeaturedImage(`new_image_${index}`);
  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };
  const handleSetFeaturedImage = (imagePath) => setFeaturedImage(imagePath);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const validationErrors = [];
    if (!formData.title?.trim()) validationErrors.push('Title is required');
    if (!formData.agencyName?.trim()) validationErrors.push('Agency name is required');
    if (!formData.city?.trim()) validationErrors.push('City/Destination is required');
    if (activityOptions.length === 0) validationErrors.push('At least one Activity Option is required');
    const totalImages = images.length + existingImages.length;
    if (totalImages === 0) validationErrors.push('At least one image is required');
    if (!formData.whatsappNumber?.trim()) validationErrors.push('WhatsApp number is required');
    if (!formData.phone?.trim()) validationErrors.push('Phone number is required');

    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('agencyName', formData.agencyName?.trim() || '');
      submitData.append('description', formData.description);
      submitData.append('shortDescription', formData.shortDescription);
      submitData.append('duration', formData.duration);
      submitData.append('suitableFor', formData.suitableFor);
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('country', formData.country);
      submitData.append('address', formData.address);
      submitData.append('displayAddress', formData.displayAddress || formData.address);
      submitData.append('latitude', formData.latitude);
      submitData.append('longitude', formData.longitude);
      submitData.append('cityLatitude', formData.cityLatitude);
      submitData.append('cityLongitude', formData.cityLongitude);
      submitData.append('activityOptions', JSON.stringify(activityOptions));
      submitData.append('availableDates', formData.availableDates);
      submitData.append('timeOptions', JSON.stringify(timeOptions));
      submitData.append('availableDatesType', formData.availableDatesType || 'all');
      submitData.append('slotConfigStartTime', formData.slotConfigStartTime || '09:00');
      submitData.append('slotConfigEndTime', formData.slotConfigEndTime || '18:00');
      submitData.append('slotConfigSlotDurationMinutes', formData.slotConfigSlotDurationMinutes || '30');
      submitData.append('specificDates', JSON.stringify(specificDates));
      submitData.append('importantInfo', formData.importantInfo);
      submitData.append('otherInfo', formData.otherInfo);
      submitData.append('whatsappNumber', formData.whatsappNumber || '');
      submitData.append('phone', isOwnerMode ? ownerPhone : (formData.phone || ''));
      submitData.append('email', formData.email || '');
      submitData.append('website', formData.website || '');
      submitData.append('currency', formData.currency);
      submitData.append('status', formData.status);
      submitData.append('featured', formData.featured);
      submitData.append('verified', formData.verified);
      submitData.append('existingImages', JSON.stringify(existingImages));

      let finalFeaturedImage = featuredImage;
      let imagesToUpload = [...images];
      if (finalFeaturedImage && String(finalFeaturedImage).startsWith('new_image_')) {
        const idx = parseInt(String(finalFeaturedImage).replace('new_image_', ''), 10);
        if (idx >= 0 && idx < imagesToUpload.length) {
          const [feat] = imagesToUpload.splice(idx, 1);
          imagesToUpload.unshift(feat);
          imagePreviews.forEach(url => URL.revokeObjectURL(url));
          finalFeaturedImage = '';
        }
      }
      if (!finalFeaturedImage && existingImages.length > 0) finalFeaturedImage = existingImages[0];
      submitData.append('featuredImage', finalFeaturedImage || '');
      imagesToUpload.forEach(file => submitData.append('images', file));

      if (isOwnerMode) {
        await axios.post(`${API_BASE_URL}/public/owners/tours`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${ownerToken}`
          }
        });
        if (onSuccess) onSuccess();
      } else if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/tours/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        navigate('/sy-admin/tours');
      } else {
        await axios.post(`${API_BASE_URL}/admin/tours`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        navigate('/sy-admin/tours');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to save tour';
      setError(msg);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = () => (isOwnerMode && onCancel ? onCancel() : navigate('/sy-admin/tours'));

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'location', label: 'City & Location' },
    { id: 'contact', label: 'Contact' },
    { id: 'options', label: 'Activity Options' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'info', label: 'Important Info' },
    { id: 'media', label: 'Media' },
    { id: 'status', label: 'Status' }
  ];

  const isLastTab = activeTab === tabs[tabs.length - 1].id;
  const currentIndex = tabs.findIndex(t => t.id === activeTab);
  const nextTab = currentIndex < tabs.length - 1 ? tabs[currentIndex + 1] : null;

  const validateTab = (tabId) => {
    const errors = [];
    if (tabId === 'basic' && !formData.title?.trim()) errors.push('Title is required');
    if (tabId === 'basic' && !formData.agencyName?.trim()) errors.push('Agency name is required');
    if (tabId === 'location' && !formData.city?.trim()) errors.push('City is required');
    if (tabId === 'contact') {
      if (!formData.whatsappNumber?.trim()) errors.push('WhatsApp number is required');
      if (!formData.phone?.trim()) errors.push('Phone number is required');
    }
    if (tabId === 'options' && activityOptions.length === 0) errors.push('At least one Activity Option is required');
    if (tabId === 'media') {
      if (images.length + existingImages.length === 0) errors.push('At least one image is required');
    }
    return errors;
  };

  const handleNext = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (isLastTab) return;
    const errs = validateTab(activeTab);
    if (errs.length > 0) {
      setError(errs.join('. '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError('');
    if (nextTab) {
      setActiveTab(nextTab.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const content = (
    <div className="container">
      <div className="tour-form-header">
        <h1>{headerTitle}</h1>
      </div>

      {error && (
        <div className="error-message" style={{
          padding: '16px 20px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
          borderRadius: '8px', color: '#991b1b', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="tour-form">
        <div className="form-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'basic' && (
            <div className="form-section">
              <h2>Basic Information</h2>
              <div className="form-group">
                <label>Title / Activity Name *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. London Eye Admission Tickets"
                  required
                />
              </div>
              <div className="form-group">
                <label>Agency name *</label>
                <input
                  type="text"
                  name="agencyName"
                  value={formData.agencyName}
                  onChange={handleChange}
                  placeholder="e.g. Sunshine Tours"
                  required
                />
              </div>
              <div className="form-group">
                <label>Short Description</label>
                <input
                  type="text"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  placeholder="Brief description for listings"
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label>About / Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={6}
                  placeholder="What the activity is, what the traveler will do and see"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    placeholder="e.g. 30 Minutes, 2 Hours"
                  />
                </div>
                <div className="form-group">
                  <label>Suitable For / Audience</label>
                  <input
                    type="text"
                    name="suitableFor"
                    value={formData.suitableFor}
                    onChange={handleChange}
                    placeholder="e.g. Adults, Children, Infants"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select name="currency" value={formData.currency} onChange={handleChange}>
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div className="form-section">
              <h2>City / Destination</h2>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>City / Destination *</label>
                <input
                  type="text"
                  value={cityInput || formData.city}
                  onChange={handleCityInputChange}
                  onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                  placeholder="Enter city or destination..."
                  required
                />
                {showCitySuggestions && citySuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {citySuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="suggestion-item"
                        onClick={() => handleCitySelect(suggestion)}
                      >
                        {suggestion.structured_formatting?.main_text || suggestion.description}
                        {suggestion.structured_formatting?.secondary_text && (
                          <div className="suggestion-secondary">
                            {suggestion.structured_formatting.secondary_text}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-hint">Start typing for city suggestions (like Hotel & Spa).</small>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>State</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group">
                <label>Address (optional)</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Meeting point or venue address"
                />
              </div>
              <div className="form-group">
                <label>Display Address</label>
                <input
                  type="text"
                  name="displayAddress"
                  value={formData.displayAddress}
                  onChange={handleChange}
                  placeholder="Address to show to users"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="number"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g. 28.6139"
                  />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="number"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="e.g. 77.2090"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City Latitude</label>
                  <input
                    type="number"
                    name="cityLatitude"
                    value={formData.cityLatitude}
                    onChange={handleChange}
                    step="any"
                  />
                </div>
                <div className="form-group">
                  <label>City Longitude</label>
                  <input
                    type="number"
                    name="cityLongitude"
                    value={formData.cityLongitude}
                    onChange={handleChange}
                    step="any"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="form-section">
              <h2>Contact Information</h2>
              <div className="form-group">
                <label>WhatsApp Number *</label>
                <input
                  type="text"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  required
                  placeholder="e.g., +919876543210"
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  readOnly={isOwnerMode}
                  placeholder="e.g., +919876543210"
                />
                {isOwnerMode && (
                  <small className="form-hint">This number is used for listing authentication.</small>
                )}
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tour@example.com"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
          )}

          {activeTab === 'options' && (
            <div className="form-section">
              <h2>Activity Options / Variants</h2>
              <p className="form-hint" style={{ marginBottom: '24px' }}>
                Add ticket types or variants (e.g. Standard Admission, Fast-Track) with pricing.
              </p>
              <div className="option-form">
                <h3>{editingOptionIndex !== null ? 'Edit Option' : 'Add Option'}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Option Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. Standard Admission, Fast-Track"
                      value={optionInput.optionName}
                      onChange={e => setOptionInput(prev => ({ ...prev, optionName: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Price per Person *</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={optionInput.pricePerPerson}
                      onChange={e => setOptionInput(prev => ({ ...prev, pricePerPerson: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cut Price (Original)</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={optionInput.cutPrice}
                      onChange={e => setOptionInput(prev => ({ ...prev, cutPrice: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cancellation Policy Tag</label>
                    <input
                      type="text"
                      placeholder="e.g. Free cancellation, Non-refundable"
                      value={optionInput.cancellationPolicyTag}
                      onChange={e => setOptionInput(prev => ({ ...prev, cancellationPolicyTag: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Option Description</label>
                  <textarea
                    placeholder="Optional description for this option"
                    value={optionInput.description}
                    onChange={e => setOptionInput(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={handleAddOption} className="btn btn-primary">
                    {editingOptionIndex !== null ? 'Update Option' : 'Add Option'}
                  </button>
                  {editingOptionIndex !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingOptionIndex(null);
                        setOptionInput({
                          optionName: '', pricePerPerson: '', cutPrice: '',
                          cancellationPolicyTag: '', description: ''
                        });
                      }}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              <div className="options-list" style={{ marginTop: '32px' }}>
                <h3>Added Options</h3>
                {activityOptions.length === 0 ? (
                  <p className="form-hint">No options yet. Add at least one.</p>
                ) : (
                  activityOptions.map((opt, idx) => (
                    <div key={idx} className="option-item">
                      <div>
                        <strong>{opt.optionName}</strong> — {formData.currency} {opt.pricePerPerson} per person
                        {opt.cutPrice != null && (
                          <span style={{ textDecoration: 'line-through', color: '#64748b', marginLeft: '8px' }}>
                            {formData.currency} {opt.cutPrice}
                          </span>
                        )}
                        {opt.cancellationPolicyTag && (
                          <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                            {opt.cancellationPolicyTag}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" onClick={() => handleEditOption(idx)} className="btn btn-secondary btn-sm">Edit</button>
                        <button type="button" onClick={() => handleRemoveOption(idx)} className="btn btn-danger btn-sm">Remove</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="form-section">
              <h2>Schedule / Date & Time (Dynamic Slots)</h2>
              <p className="form-hint" style={{ marginBottom: 16 }}>
                Set available dates and a time window with slot duration. Slots will be generated automatically for users to choose when booking.
              </p>
              <div className="form-group">
                <label>Available dates</label>
                <select
                  name="availableDatesType"
                  value={formData.availableDatesType || 'all'}
                  onChange={handleChange}
                >
                  <option value="all">All dates (any day)</option>
                  <option value="specific">Specific dates only</option>
                </select>
              </div>
              {formData.availableDatesType === 'specific' && (
                <div className="form-group">
                  <label>Add specific dates (YYYY-MM-DD)</label>
                  <div className="amenity-input">
                    <input
                      type="date"
                      value={specificDateInput}
                      onChange={e => setSpecificDateInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddSpecificDate())}
                    />
                    <button type="button" onClick={handleAddSpecificDate} className="btn btn-secondary">Add date</button>
                  </div>
                  <div className="amenity-list" style={{ marginTop: 8 }}>
                    {specificDates.map((d, idx) => (
                      <span key={idx} className="badge badge-primary">
                        {d}
                        <button type="button" onClick={() => handleRemoveSpecificDate(idx)} className="remove-btn">×</button>
                      </span>
                    ))}
                    {specificDates.length === 0 && (
                      <span className="form-hint">No dates added. Add at least one date for &quot;Specific dates only&quot;.</span>
                    )}
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Slot start time</label>
                  <input
                    type="time"
                    name="slotConfigStartTime"
                    value={formData.slotConfigStartTime || '09:00'}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Slot end time</label>
                  <input
                    type="time"
                    name="slotConfigEndTime"
                    value={formData.slotConfigEndTime || '18:00'}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Slot duration (minutes)</label>
                <input
                  type="number"
                  name="slotConfigSlotDurationMinutes"
                  value={formData.slotConfigSlotDurationMinutes || '30'}
                  onChange={handleChange}
                  min={5}
                  max={240}
                  step={5}
                  placeholder="e.g. 30"
                />
                <small className="form-hint">Between 5 and 240 minutes. Slots will be generated from start to end (e.g. 09:00–09:30, 09:30–10:00).</small>
              </div>
              <div className="form-group">
                <label>Generated slots preview (example for one day)</label>
                <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8, marginTop: 4 }}>
                  {generateSlotPreview().length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {generateSlotPreview().map((slot, idx) => (
                        <span key={idx} className="badge badge-secondary">{slot}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="form-hint">Adjust start time, end time and duration to generate slots.</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'info' && (
            <div className="form-section">
              <h2>Important Information</h2>
              <div className="form-group">
                <label>Important Info (meeting point, guidelines, restrictions)</label>
                <textarea
                  name="importantInfo"
                  value={formData.importantInfo}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Key info travelers must know before booking"
                />
              </div>
              <div className="form-group">
                <label>Other Info / Additional Details (terms, FAQs)</label>
                <textarea
                  name="otherInfo"
                  value={formData.otherInfo}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Extra sections like terms, FAQs"
                />
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <div className="form-section">
              <h2>Images / Gallery</h2>
              <div className="form-group">
                <label>Upload Images *</label>
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    id="tour-image-upload"
                    className="image-upload-input"
                  />
                  <label htmlFor="tour-image-upload" className="image-upload-label">
                    <div className="image-upload-content">
                      <span className="image-upload-icon">📷</span>
                      <span className="image-upload-text">Click to upload or drag and drop</span>
                      <span className="image-upload-hint">PNG, JPG, GIF up to 5MB</span>
                    </div>
                  </label>
                </div>
              </div>
              {imagePreviews.length > 0 && (
                <div className="images-preview-section">
                  <h3>New images</h3>
                  <div className="images-grid">
                    {imagePreviews.map((preview, idx) => (
                      <div key={idx} className="image-preview-card">
                        <div className="image-preview-wrapper">
                          <img src={preview} alt={`Preview ${idx + 1}`} />
                          {featuredImage === `new_image_${idx}` && (
                            <div className="featured-badge-overlay"><span>⭐ Featured</span></div>
                          )}
                        </div>
                        <div className="image-preview-actions">
                          <button
                            type="button"
                            onClick={() => handleSetFeaturedFromNew(idx)}
                            className={`btn btn-sm ${featuredImage === `new_image_${idx}` ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {featuredImage === `new_image_${idx}` ? '⭐ Featured' : 'Set Featured'}
                          </button>
                          <button type="button" onClick={() => handleRemoveNewImage(idx)} className="btn btn-sm btn-danger">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {existingImages.length > 0 && (
                <div className="images-preview-section">
                  <h3>Existing images</h3>
                  <div className="images-grid">
                    {existingImages.map((image, idx) => (
                      <div key={idx} className="image-preview-card">
                        <div className="image-preview-wrapper">
                          <img src={`${API_BASE_URL_WITHOUT_API}${image}`} alt={`Tour ${idx + 1}`} />
                          {featuredImage === image && (
                            <div className="featured-badge-overlay"><span>⭐ Featured</span></div>
                          )}
                        </div>
                        <div className="image-preview-actions">
                          <button
                            type="button"
                            onClick={() => handleSetFeaturedImage(image)}
                            className={`btn btn-sm ${featuredImage === image ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {featuredImage === image ? '⭐ Featured' : 'Set Featured'}
                          </button>
                          <button type="button" onClick={() => handleRemoveExistingImage(idx)} className="btn btn-sm btn-danger">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {imagePreviews.length === 0 && existingImages.length === 0 && (
                <p className="form-hint" style={{ marginTop: '16px' }}>Upload at least one image.</p>
              )}
            </div>
          )}

          {activeTab === 'status' && (
            <div className="form-section">
              <h2>Status & Flags</h2>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                  />
                  <span>Featured</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="verified"
                    checked={formData.verified}
                    onChange={handleChange}
                  />
                  <span>Verified</span>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          {isLastTab ? (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Tour' : 'Create Tour'}
            </button>
          ) : (
            <button type="button" onClick={handleNext} className="btn btn-primary">Next</button>
          )}
          <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
        </div>
      </form>
    </div>
  );

  return isOwnerMode ? content : <Layout>{content}</Layout>;
};

export default TourForm;
